<script lang="ts">
  import Icon from './Icon.svelte';

  let {
    value = $bindable(''),
    id = undefined,
    name = undefined,
    label = undefined,
    placeholder = '',
    autocomplete = 'current-password',
    required = false,
    disabled = false,
    minlength = undefined,
    maxlength = undefined,
    class: className = '',
  }: {
    value?: string;
    id?: string;
    name?: string;
    label?: string;
    placeholder?: string;
    autocomplete?: string;
    required?: boolean;
    disabled?: boolean;
    minlength?: number;
    maxlength?: number;
    class?: string;
  } = $props();

  /** Visible only while the eye is pressed/held — release hides again. */
  let visible = $state(false);

  function show() {
    if (disabled) return;
    visible = true;
  }

  function hide() {
    visible = false;
  }

  function onKeyDown(e: KeyboardEvent) {
    if (disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      visible = true;
    }
  }

  function onKeyUp(e: KeyboardEvent) {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      visible = false;
    }
  }
</script>

{#if label}
  <label class="pw-field {className}" for={id}>
    <span class="pw-label">{label}</span>
    <div class="pw-wrap">
      <input
        {id}
        {name}
        class="pw-input"
        type={visible ? 'text' : 'password'}
        bind:value
        {placeholder}
        {autocomplete}
        {required}
        {disabled}
        minlength={minlength}
        maxlength={maxlength}
      />
      <button
        type="button"
        class="pw-toggle"
        tabindex="0"
        aria-label="Hold to show password"
        aria-pressed={visible}
        {disabled}
        onpointerdown={show}
        onpointerup={hide}
        onpointerleave={hide}
        onpointercancel={hide}
        onblur={hide}
        onkeydown={onKeyDown}
        onkeyup={onKeyUp}
        oncontextmenu={(e) => e.preventDefault()}
      >
        <Icon name={visible ? 'eyeOff' : 'eye'} size={18} />
      </button>
    </div>
  </label>
{:else}
  <div class="pw-wrap {className}">
    <input
      {id}
      {name}
      class="pw-input"
      type={visible ? 'text' : 'password'}
      bind:value
      {placeholder}
      {autocomplete}
      {required}
      {disabled}
      minlength={minlength}
      maxlength={maxlength}
    />
    <button
      type="button"
      class="pw-toggle"
      tabindex="0"
      aria-label="Hold to show password"
      aria-pressed={visible}
      {disabled}
      onpointerdown={show}
      onpointerup={hide}
      onpointerleave={hide}
      onpointercancel={hide}
      onblur={hide}
      onkeydown={onKeyDown}
      onkeyup={onKeyUp}
      oncontextmenu={(e) => e.preventDefault()}
    >
      <Icon name={visible ? 'eyeOff' : 'eye'} size={18} />
    </button>
  </div>
{/if}

<style>
  .pw-field {
    display: grid;
    gap: 0.3rem;
    font-size: inherit;
  }

  .pw-label {
    color: #64748b;
    font-weight: 500;
  }

  .pw-wrap {
    position: relative;
    display: flex;
    align-items: stretch;
  }

  /* Force light field on invite/login white cards — do not inherit dark theme tokens. */
  .pw-input {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    padding: 0.6rem 2.5rem 0.6rem 0.75rem;
    background: #ffffff !important;
    color: #0f172a !important;
    caret-color: #0f172a;
    font: inherit;
    -webkit-text-fill-color: #0f172a;
  }

  .pw-input::placeholder {
    color: #94a3b8;
    -webkit-text-fill-color: #94a3b8;
  }

  .pw-input:focus {
    outline: 2px solid color-mix(in srgb, #2563eb 45%, transparent);
    outline-offset: 1px;
    border-color: #93c5fd;
  }

  .pw-input:disabled {
    opacity: 0.65;
    background: #f8fafc !important;
  }

  /* Autofill can paint dark/yellow — keep readable */
  .pw-input:-webkit-autofill,
  .pw-input:-webkit-autofill:hover,
  .pw-input:-webkit-autofill:focus {
    -webkit-text-fill-color: #0f172a !important;
    box-shadow: 0 0 0 1000px #ffffff inset !important;
    transition: background-color 99999s ease-out;
  }

  .pw-toggle {
    position: absolute;
    right: 0.3rem;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: none;
    border-radius: 0.4rem;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    padding: 0;
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
  }

  .pw-toggle:hover:not(:disabled) {
    color: #0f172a;
    background: #f1f5f9;
  }

  .pw-toggle:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
